import { createJSONStorage, type PersistStorage } from "zustand/middleware";

/**
 * The `storage` adapter every `persist`-ed store in this repo passes EXPLICITLY.
 *
 * ⚠ WHY THIS EXISTS — omitting `storage` is not a shortcut, it is a bug.
 * zustand's default is `createJSONStorage(() => window.localStorage)`. On the
 * server `window` is undefined, so that throws, `createJSONStorage` swallows it
 * and returns `undefined`, and `persist` then takes its "no storage" branch:
 * it silently wraps `set` in a function that `console.warn`s
 *
 *     [zustand persist middleware] Unable to update item '<name>',
 *     the given storage is currently unavailable.
 *
 * on EVERY call. That is the noise the store specs used to emit — a symptom, not
 * a quirk. Handing `persist` a real (no-op) storage on the server keeps the
 * middleware on its normal path, and the CLIENT bytes are unchanged: same key,
 * same `JSON.stringify({ state, version })` envelope, same Web Storage object.
 *
 * See `.claude/rules/state-management.md` § "Persistence + hydration".
 *
 * NOT for a store whose key is read by something outside React — a bare-string
 * contract needs its own `PersistStorage` (see `barePrefStorage` in
 * `stores/theme-store.ts`, read pre-paint by `THEME_INIT_SCRIPT`).
 */

/**
 * A `Storage` that stores nothing. A FACTORY, not a shared literal: a store is a
 * module singleton and a Workers isolate is reused across requests, so anything
 * a store holds onto must not be shared mutable state.
 */
function createSsrNoopStorage(): Storage {
  return {
    length: 0,
    clear: () => {},
    key: () => null,
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
  } satisfies Storage;
}

/**
 * JSON-envelope storage backed by `localStorage` (survives tab close) or
 * `sessionStorage` (per tab), degrading to a no-op on the server.
 */
export function browserJSONStorage<T>(
  kind: "local" | "session",
): PersistStorage<T> | undefined {
  return createJSONStorage<T>(() => {
    if (typeof window === "undefined") return createSsrNoopStorage();
    return kind === "session" ? window.sessionStorage : window.localStorage;
  });
}
